import * as React from 'react';
import { Alert, Button, Grid, Tooltip } from '@mui/material';
import { HeaderSection, setColorStyle } from './BiasInfo';
import { useEffect } from 'react';
import { updateIndividualArea } from '../../../actions/individualAreas';
import {
	handleError,
	handleSuccess,
	handleWarning,
} from '../../../helpers/utils';
import { getDataByUrl } from '../../../actions/catalogs';
import { updateBiasLogistics } from '../../../actions/biases';
import { diffDaysOrder } from '../../../containers/OrderTracking';
import { DraggableAreaList } from './DraggableAreasList';

const errorMsg = 'Hubo un error al intentar editar el área. Intenta más tarde';

export const AllowedAreas = ({
	order = null,
	individualAreas = [],
	setIndividualAreas = null,
	setCurrentBias = null,
	setCurrentSubbias = null,
	currentSubbias = null,
	bias = null,
}) => {
	const [checked, setChecked] = React.useState([]);
	const [currentAreaItem, setCurrentAreaItem] = React.useState(null);
	const [editing, setEditing] = React.useState(false);
	const [newDay, setNewDay] = React.useState(0);
	const [msgText, setMsgText] = React.useState('');
	const [isMsgSent, setIsMsgSent] = React.useState(null);
	const [diffDays, setDiffDays] = React.useState(0);
	const [daysTotal, setDaysTotal] = React.useState(0);
	const [items, setItems] = React.useState([]);

	const printItems = (array = items) => {
		array?.forEach((item) =>
			console.log(
				`${item.active ? '✅' : '❌'} ${item.indexOrder}.- ${item.name}`
			)
		);
	};

	const handleSortAreasByIndexOrder = (items) =>
		items?.sort((a, b) => a.indexOrder - b.indexOrder);

	// Sort areas and set their blocking area
	const handleSortAreasDragAndDrop = (
		items,
		justArray = false,
		updateItems = true
	) => {
		// Rearrange areas
		let rearreangedAreas = items
			// .filter((item) => item.active)
			.map((item, index) => ({
				...item,
				indexOrder: index + 1,
				blocked_by:
					items[index - 1] && item.active
						? items[index - 1].name
						: '-',
			}));

		// Sort areas by indexOrder
		rearreangedAreas = handleSortAreasByIndexOrder(rearreangedAreas);

		// Sort areas by indexOrder
		if (updateItems) setItems(rearreangedAreas);

		// Return just array
		if (justArray) return rearreangedAreas;

		// Handle update all areas
		handleUpdateAllAreas(rearreangedAreas);
	};

	// Update status active/inactive
	const handleToggleArea = (value) => async () => {
		const currentIndex = checked.indexOf(value);
		const newChecked = [...checked];
		const currentArea = items.find((item) => item.id === value);

		const { ok: canDisallow = true, count = 0 } =
			await handleCheckCanDisallowArea();

		if (canDisallow) {
			updateIndividualArea(value, {
				...currentArea,
				active: currentIndex === -1,
			})
				.then(({ data }) => {
					updateIndividualAreasState(value, data);
					setCurrentSubbias(
						currentSubbias === null
							? null
							: { ...currentSubbias, updated: Date.now() }
					);
				})
				.catch((error) => {
					feedbackMessage(false, errorMsg);
				});

			if (currentIndex === -1) {
				newChecked.push(value);
			} else {
				newChecked.splice(currentIndex, 1);
			}

			setChecked(newChecked);
		} else {
			feedbackMessage(
				false,
				`No se puede quitar esta área porque hay ${count} proceso(s) que puede(n) depender de ella.`,
				7
			);
		}
	};

	// Update days total
	const updateDaysTotal = (updatedAreas) => {
		const daysAllowed = updatedAreas.reduce(
			(sum, item) => sum + item.days,
			0
		);
		setDaysTotal(daysAllowed);
	};

	// Update state of individual areas (located in BiasInfo.js)
	const updateIndividualAreasState = (areaId, newData) => {
		// Create a copy of the individualAreas array to avoid direct mutations
		let newIndividualAreas = items;

		// Map the IDs of each item in newIndividualAreas and find the index of the provided areaId
		const index = newIndividualAreas.map((item) => item.id).indexOf(areaId);

		// Update the object at the found index with the new data
		newIndividualAreas[index] = newData;

		// Sort the areas according to a specific order defined in handleSortAreasDragAndDrop
		newIndividualAreas = handleSortAreasDragAndDrop(
			newIndividualAreas,
			true
		);

		// Update the total days based on the updated individual areas
		updateDaysTotal(newIndividualAreas?.filter((item) => item.active));

		// Show a success message indicating the area has been edited successfully
		handleSuccess('Área editada', true, 2, 'top');
	};

	// Feedback message control
	const feedbackMessage = (ok, msg, seconds = 4) => {
		setIsMsgSent(ok);
		setMsgText(msg);
		setTimeout(() => {
			setIsMsgSent(null);
			setMsgText('');
		}, seconds * 1000);
	};

	// Update all areas
	const handleUpdateAllAreas = (newItems) => {
		const promises = newItems.map((item) =>
			updateIndividualArea(item.id, item)
		);
		Promise.all(promises).catch((error) => {
			console.log(error);
			feedbackMessage(false, errorMsg);
		});
	};

	// Update day
	const handleUpdateDay = (areaId) => {
		const currentArea = items.find((item) => item.id === areaId);
		updateIndividualArea(areaId, {
			...currentArea,
			days: parseInt(newDay),
		})
			.then(({ data }) => {
				updateIndividualAreasState(areaId, data);
				setCurrentAreaItem(null);
				setCurrentSubbias(
					currentSubbias === null
						? null
						: { ...currentSubbias, updated: Date.now() }
				);
			})
			.catch((error) => {
				console.log(error);
				feedbackMessage(false, errorMsg);
			});
	};

	const handleSaveBias = () => {
		// Check if the total days match the expected difference in days
		if (daysTotal <= diffDays) {
			// Call function to handle sorting areas after drag and drop operation
			handleSortAreasDragAndDrop(items, false, false);

			// Update the bias logistics by calling the API with the bias ID
			updateBiasLogistics(bias.id)
				.then(() => {
					// Show a success message upon successful update
					handleSuccess('Partida actualizada', true, 2, 'top');

					// Update the currentBias state to mark logistics as done
					setCurrentBias((old) => ({
						...old,
						logistics_is_done: true,
					}));
				})
				.catch((error) => {
					// Handle any errors that occur during the update process
					handleError(
						error,
						'Ocurrió un error al intentar actualizar la partida, comunicate con el equipo de sistemas'
					);
				});
		} else {
			// Show a warning if the total days do not match the delivery days
			handleWarning(
				`Los días asignados (${daysTotal}) no coinciden con los días de entrega (${diffDays})`
			);
		}
	};

	// Handle check if an especific area can be "Disallowed"
	const handleCheckCanDisallowArea = async () => {
		const data = (await getDataByUrl(`/api/bias/process/?bias=${bias.id}`))
			.data;
		return { ok: data.count > 0 ? false : true, count: data.count };
	};

	// Set active areas
	useEffect(() => {
		// Check if there are any individual areas
		if (individualAreas.length > 0) {
			// Initialize arrays to hold active areas and their IDs
			let activeOnes = [];
			let activeOnesIds = [];

			const finalArray = items?.length > 0 ? items : individualAreas;
			// const finalArray = individualAreas;

			// Filter the individualAreas to get only the active items
			activeOnes = finalArray.filter((item) => item.active);

			// Map the active items to get their IDs
			activeOnesIds = activeOnes.map((item) => item.id);

			// Update the items state with the individualAreas (though it remains unchanged here)
			// FIXME: do I need this?
			handleSortAreasDragAndDrop(finalArray, true);

			// Update the total days based on the active items
			updateDaysTotal(activeOnes);

			// Set the checked state with the IDs of the active items
			setChecked(activeOnesIds);
		}
	}, [individualAreas]); // Dependency array: runs this effect when individualAreas changes

	useEffect(() => {
		setDiffDays(diffDaysOrder(order?.entry_date, order?.delivery_date));
	}, []);

	useEffect(() => {
		if (individualAreas.length > 0 && bias?.id) {
			let activeOnes = [];
			let activeOnesIds = [];

			const finalArray = individualAreas;

			activeOnes = finalArray.filter((item) => item.active);
			activeOnesIds = activeOnes.map((item) => item.id);

			handleSortAreasDragAndDrop(finalArray, true);
			updateDaysTotal(activeOnes);
			setChecked(activeOnesIds);
		}
	}, [bias?.id, individualAreas]);

	// Handle edit day
	const handleOnClickEdit = (areaItem) => {
		if (editing) {
			handleUpdateDay(areaItem.id);
		} else {
			setCurrentAreaItem(areaItem.id);
			setNewDay(areaItem.days);
		}
		setEditing(!editing);
	};

	return (
		<Grid
			item
			xs={4}
			style={{
				...setColorStyle('green'),
				height: '100%',
			}}
		>
			<Grid
				container
				style={{
					...setColorStyle('red'),
					height: '100%',
				}}
			>
				<Grid
					item
					xs={12}
					style={{
						...setColorStyle('blue'),
						padding: '15px',
					}}
				>
					<HeaderSection text='Áreas permitidas' />
					<div style={{ textAlign: 'center' }}>
						<small>
							Solo se pueden activar y desactivar áreas antes de
							pasar a producción
						</small>
					</div>
					{isMsgSent !== null && msgText.length > 0 && (
						<Alert
							severity={isMsgSent ? 'success' : 'error'}
							style={{ margin: '30px 0 0 0' }}
						>
							{msgText}
						</Alert>
					)}
					<div
						style={{
							margin: '10px 0 0 0',
							maxHeight: '70vh',
							overflow: 'scroll',
							overflowX: 'hidden',
						}}
					>
						{items?.length > 0 && (
							<DraggableAreaList
								items={items} // New state for ordering (using drag and drop)
								setItems={setItems}
								handleOnClickEdit={handleOnClickEdit}
								checked={checked}
								newDay={newDay}
								setNewDay={setNewDay}
								currentAreaItem={currentAreaItem}
								editing={editing}
								handleToggleArea={handleToggleArea}
								printItems={printItems}
							/>
						)}
					</div>
					<Tooltip title='Total de días seleccionados / Días configurados para la entrega'>
						<Button
							variant='text'
							color='primary'
							onClick={handleSaveBias}
						>
							Guardar partida ({daysTotal}/{diffDays})
						</Button>
					</Tooltip>
				</Grid>
			</Grid>
		</Grid>
	);
};
